/**
 * Copyright 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo, useState } from "react";
import { Else, If, Then, When } from "react-if";
import styled from "styled-components";
import {
  Box,
  BrickLoader,
  Button,
  FlexContainer,
  Table,
  theme,
} from "@nordcloud/gnui";
import { Group } from "~/generated/graphql";
import { LoaderWrap } from "~/components";
import { CreateResourceGroupItem } from "~/components/resourceGroup/CreateResourceGroupItem";
import { DuplicateResourceIcon } from "~/components/resourceGroup/DuplicateResourceIcon";
import { ResourceGroupItem } from "~/components/resourceGroup/ResourceGroupItem";
import { ResourceItem } from "~/components/resourceGroup/ResourceItem";
import { ResourcesNoData } from "~/components/resourceGroup/ResourcesNoData";
import { useGetResources } from "~/hooks";
import { useDuplicatedResources } from "~/hooks/useDuplicatedResources";
import { isNotEmpty, isNotNil } from "~/tools";
import {
  PlanData,
  PlanField,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/constants";
import { usePlanWizard } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanProvider";
import { AddResources } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/AddResources/AddResources";
import { CreateResourceGroupModal } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/AddResources/CreateResourceGroupModal";
import { updateEntities } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/utils";
import { useStepState } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/StepProvider";
import {
  CreateResourceGroup,
  SelectedPlanEntity,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/types";
import { useGetAllResourceGroups } from "~/views/resourceGroups/hooks";

export function ActionResources() {
  const { stepState, updateStepState } = useStepState();
  const { setPlanData } = usePlanWizard();

  const [resourceGroupModalOpen, setResourceGroupModalOpen] = useState(false);
  const [modalDefaultValues, setModalDefaultValues] = useState<
    CreateResourceGroup | undefined
  >(undefined);

  const {
    getGroups,
    resourceGroups,
    loading: loadingGroups,
    refetch: refetchResourceGroups,
  } = useGetAllResourceGroups({ basic: true });

  const {
    getResources,
    resources,
    loading: loadingResources,
  } = useGetResources();

  const selectedEntityGroups =
    stepState.selectedEntity?.resourceGroupIds
      ?.map((id) => resourceGroups.find((group) => group.id === id))
      .filter(Boolean)
      .filter(isNotNil) ?? [];

  const resourceGroupsToCreate = useMemo(() => {
    return (
      stepState.selectedEntity?.createResourceGroups?.map((group) => {
        const resourceIds = group?.resourceIds ?? [];
        return {
          listId: group.listId,
          name: group.name,
          description: group.description,
          metadata: group.metadata,
          resources: resourceIds
            .map((id) => resources.find((resource) => resource.id === id))
            .filter(Boolean)
            .filter(isNotNil),
        };
      }) ?? []
    );
  }, [stepState.selectedEntity, resources]);

  const updatePlanDataWithEntities = (
    updatedEntities: SelectedPlanEntity[] | undefined,
    additional: Partial<PlanData[PlanField.PLAN_SETTINGS]> = {}
  ) => {
    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.PLAN_SETTINGS]: {
        ...prevPlanData[PlanField.PLAN_SETTINGS],
        planEntities: updatedEntities,
        ...additional,
      },
    }));
  };

  const handleRemoveResourceGroupToCreate = (groupListId: string) => {
    const updatedGroups =
      stepState.selectedEntity?.createResourceGroups?.filter(
        (group) => group.listId !== groupListId
      );
    const updatedEntities = updateEntities(stepState, {
      createResourceGroups: updatedGroups,
    });

    updateStepState({
      entities: updatedEntities,
      selectedEntity: {
        ...stepState.selectedEntity,
        createResourceGroups: updatedGroups,
      },
    });

    updatePlanDataWithEntities(updatedEntities, {
      createResourceGroups: updatedGroups,
    });
  };

  const handleOpenResourceGroupEdit = (
    group: typeof resourceGroupsToCreate[number]
  ) => {
    setModalDefaultValues({
      ...group,
      resourceIds: group.resources.map((r) => r.id),
    });
    setResourceGroupModalOpen(true);
  };

  const handleApplyResourceGroupEdit = (
    editResourceGroup: CreateResourceGroup
  ) => {
    const updatedGroup = {
      ...editResourceGroup,
      name: editResourceGroup.name,
      description: editResourceGroup.description,
      metadata: editResourceGroup.metadata,
    };

    const updatedGroups = stepState.selectedEntity?.createResourceGroups?.map(
      (group) => {
        if (group.listId === editResourceGroup.listId) {
          return updatedGroup;
        }
        return group;
      }
    );

    const updatedEntities = updateEntities(stepState, {
      createResourceGroups: updatedGroups,
    });

    updateStepState({
      entities: updatedEntities,
      selectedEntity: {
        ...stepState.selectedEntity,
        createResourceGroups: updatedGroups,
      },
    });

    const planDataAlreadyCreatedGroups =
      stepState.selectedEntity?.createResourceGroups?.filter(
        (group) => group.listId !== editResourceGroup.listId
      ) ?? [];

    const planDataUpdatedGroups = [
      ...planDataAlreadyCreatedGroups,
      updatedGroup,
    ];

    updatePlanDataWithEntities(updatedEntities, {
      createResourceGroups: planDataUpdatedGroups,
    });

    setResourceGroupModalOpen(false);
  };

  const selectedEntityResources = useMemo(() => {
    return (
      stepState.selectedEntity?.resourceIds
        ?.map((id) => resources.find((resource) => resource.id === id))
        .filter(Boolean)
        .filter(isNotNil) ?? []
    );
  }, [stepState.selectedEntity, resources]);

  const newGroupResources = useMemo(
    () => resourceGroupsToCreate.flatMap((group) => group.resources),
    [resourceGroupsToCreate]
  );

  const duplicateState = useDuplicatedResources({
    resourceCollections: [selectedEntityResources, newGroupResources],
    actionChangeKey: stepState.selectedEntity?.id,
  });

  type RemoveResourceIds = {
    resourceGroupIds?: string[];
    resourceIds?: string[];
  };

  const handleRemove = (updatedIds: RemoveResourceIds) => {
    const updatedEntities = updateEntities(stepState, updatedIds);

    updateStepState({
      entities: updatedEntities,
      selectedEntity: {
        ...stepState.selectedEntity,
        ...updatedIds,
      },
    });

    updatePlanDataWithEntities(updatedEntities);
  };

  const handleRemoveResourceGroup = (groupId: string) => {
    handleRemove({
      resourceGroupIds: stepState.selectedEntity?.resourceGroupIds?.filter(
        (id) => id !== groupId
      ),
    });

    duplicateState.onGroupLoad(groupId, []);
  };
  const handleRemoveResource = async (resourceId: string) => {
    handleRemove({
      resourceIds: stepState.selectedEntity?.resourceIds?.filter(
        (id) => id !== resourceId
      ),
    });
  };

  useEffect(() => {
    getGroups();
    getResources();
  }, [getGroups, getResources]);

  return (
    <FlexContainer
      css={{ flex: 1, minWidth: "20rem" }}
      direction="column"
      gap={`${theme.spacing.spacing02}`}
    >
      <Box>
        <AddResources
          refetchResourceGroups={refetchResourceGroups}
          refetchResources={getResources}
          alreadyAddedResourceIds={duplicateState.allAddedResourceIds}
        />
      </Box>

      <LoaderWrap
        loading={loadingGroups || loadingResources}
        Component={BrickLoader}
        css={{ height: "100%" }}
      >
        <If
          condition={
            isNotEmpty(selectedEntityGroups) ||
            isNotEmpty(selectedEntityResources) ||
            isNotEmpty(resourceGroupsToCreate)
          }
        >
          <Then>
            {selectedEntityGroups.filter(isNotNil).map((resourceGroup) => {
              return (
                <ResourceGroupItem
                  key={resourceGroup.id}
                  resourceGroup={resourceGroup as Group}
                  actions={() => (
                    <Button
                      severity="low"
                      size="md"
                      icon="trash"
                      onClick={() =>
                        handleRemoveResourceGroup(resourceGroup.id)
                      }
                    />
                  )}
                  duplicateState={duplicateState}
                />
              );
            })}
            {resourceGroupsToCreate.map((group) => {
              return (
                <CreateResourceGroupItem
                  key={group.listId}
                  resourceGroup={group}
                  actions={() => (
                    <>
                      <Button
                        severity="low"
                        size="md"
                        icon="edit"
                        onClick={() => handleOpenResourceGroupEdit(group)}
                      />
                      <Button
                        severity="low"
                        size="md"
                        icon="trash"
                        onClick={() =>
                          handleRemoveResourceGroupToCreate(group.listId)
                        }
                      />
                    </>
                  )}
                  duplicateState={duplicateState}
                />
              );
            })}
            <WrapResources>
              {selectedEntityResources.map((resource) => {
                return (
                  <Box
                    key={resource.id}
                    innerSpacing="spacing00"
                    css={{ marginBottom: theme.spacing.spacing02 }}
                  >
                    {/* TODO: Remove usage of Table for single resource */}
                    <Table>
                      <Table.tbody>
                        <ResourceItem
                          resource={resource}
                          highlight={duplicateState.isHighlighted(resource.id)}
                          actions={() => (
                            <FlexContainer gap={theme.spacing.spacing02}>
                              {duplicateState.duplicateResourceIds?.has(
                                resource.id
                              ) && (
                                <DuplicateResourceIcon
                                  resourceId={resource.id}
                                  onIconClick={
                                    duplicateState.onDuplicateIconClick
                                  }
                                />
                              )}
                              <Button
                                severity="low"
                                size="md"
                                icon="trash"
                                onClick={() =>
                                  handleRemoveResource(resource.id)
                                }
                              />
                            </FlexContainer>
                          )}
                        />
                      </Table.tbody>
                    </Table>
                  </Box>
                );
              })}
            </WrapResources>
          </Then>
          <Else>
            <ResourcesNoData />
          </Else>
        </If>
      </LoaderWrap>
      <When condition={resourceGroupModalOpen}>
        <CreateResourceGroupModal
          onApply={handleApplyResourceGroupEdit}
          onCancel={() => setResourceGroupModalOpen(false)}
          isOpen={resourceGroupModalOpen}
          defaultValues={modalDefaultValues}
        />
      </When>
    </FlexContainer>
  );
}

const WrapResources = styled.div`
  margin-top: ${theme.spacing.spacing02};
  width: 100%;
`;
